import { useRef, useState, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { EuiGlobalToastList } from "@elastic/eui";
import { createUseStyles } from "react-jss";
import Page from "../Page/Page";
import ErrorFallback from "../ErrorFallback/ErrorFallback";
import DownOrOffline from "pages/ServicePages/DownOrOffline";
import HealthService from "services/UtilsService/HealthService";
import Emitter from "services/UtilsService/Emitter";
import { initializePostHog } from "../../PostHog";
import { APP_STATUS, toastLifeTimeMs } from "Consts";

const useStyles = createUseStyles({
  toastCenter: {
    position: "fixed",
    left: "50% !important",
    right: "auto !important",
    bottom: "20px !important",
    top: "auto !important",
    transform: "translateX(-50%)",
    zIndex: 10000, // optional: ensure it's above other content
  },
});

const withRouter = (Component: any) => {
  const Wrapper = (props: any) => {
    return <Component {...props} />;
  };
  return Wrapper;
};

const Layout = ({ children }: any) => {
  const classes = useStyles();
  const globalMessageId = useRef(1);
  const healthIntervalRef = useRef<number | null>(null);
  const versionIntervalRef = useRef<number | null>(null);

  const [isDown, setIsDown] = useState<boolean>(false);
  const [status, setStatus] = useState<string>(APP_STATUS.healthy);
  const [toastMessages, setToastMessages] = useState([]);

  const getNextMessageId = () => {
    if (globalMessageId.current > 1000000) {
      globalMessageId.current = 0;
    }
    globalMessageId.current = globalMessageId.current + 1;
    return `${globalMessageId.current}`;
  };

  const appDownMonitor = (response: any) => {
    if (response === "healthy") {
      setIsDown(false);
      setStatus(APP_STATUS.healthy);
    }
    if (response?.status === 500) {
      setIsDown(true);
      setStatus(APP_STATUS.down);
    }
    if (response?.errors?.length > 0) {
      setIsDown(true);
      setStatus(APP_STATUS.offline);
    }
  };

  async function getVersion() {
    const url = `${window.location.origin}/v`;
    try {
      const response = await fetch(url, {
        headers: {
          "Cache-Control": "max-age=0",
          Pragma: "no-cache",
          "If-None-Match": localStorage.getItem("_eTag") || "",
        },
      });

      // If server supports ETag properly
      const newETag = response.headers.get("etag");
      if (newETag) {
        localStorage.setItem("_eTag", newETag);
      }

      // If nothing changed, server may respond 304
      if (response.status === 304) return;

      if (!response.ok) {
        throw new Error(`Version check failed: ${response.status}`);
      }

      const result: any = await response.json();
      const prevVersion = localStorage.getItem("build_version");

      if (result?.version && result?.version !== prevVersion) {
        localStorage.setItem("build_version", result?.version);

        // stop everything before reload
        if (healthIntervalRef.current) clearInterval(healthIntervalRef.current);

        if (versionIntervalRef.current) clearInterval(versionIntervalRef.current);

        window.location.reload();
      }
    } catch (err) {
      console.error("Version check error:", err);
    }
  }

  useEffect(() => {
    if (window.location.origin.includes("localhost")) return;

    healthIntervalRef.current = window.setInterval(() => {
      HealthService.getHealthStatus().then(appDownMonitor);
    }, 15000);

    // run immediately once
    getVersion();

    versionIntervalRef.current = window.setInterval(getVersion, 15000);

    return () => {
      if (healthIntervalRef.current) clearInterval(healthIntervalRef.current);

      if (versionIntervalRef.current) clearInterval(versionIntervalRef.current);
    };
  }, []);

  const customDismissToast = (toastId: any) => {
    setTimeout(() => {
      setToastMessages((prev) => prev.filter((t: any) => t.id !== toastId));
    }, 5000);
  };

  // const handleChunkloadError = () => {
  //   window.addEventListener("error", (event) => {
  //     if (event.message?.includes("ChunkLoadError") || event.message?.includes("Loading chunk")) {
  //       window.location.reload();
  //     }
  //   });
  // };

  useEffect(() => {
    // handleChunkloadError();

    Emitter.on("displayFailure", (msg: any) => {
      setToastMessages((arr: any) => {
        if (arr.length > 0) return arr; //dont show 2 errors togther
        if (arr.find((m: any) => m.text == msg)) {
          return arr;
        }

        return [
          ...arr,
          {
            id: getNextMessageId(),
            title: "Failure",
            color: "danger",
            iconType: "help",
            text: msg,
            "data-test-subj": "toastError",
          },
        ];
      });
    });

    Emitter.on("displaySuccess", (msg: any) => {
      const toastId = getNextMessageId();
      setToastMessages((arr: any) => {
        if (arr.find((m: any) => m.text == msg)) {
          return arr;
        }
        //debugger;
        return [
          ...arr,
          {
            id: toastId,
            title: "Success",
            color: "success",
            iconType: "check",
            text: msg,
          },
        ];
      });

      customDismissToast(toastId);
    });

    Emitter.on("displayApiErrors", (errors: any) => {
      setToastMessages((arr: any) => {
        if (arr.length > 0) return arr; //dont show 2 errors togther
        const newMsgs = (errors || [])
          .map((error: { message: any; title: any }) => {
            if (error.message && !arr.find((m: any) => m.text == error.message)) {
              return {
                title: error.title || "Error",
                color: "danger",
                iconType: "help",
                text: error.message,
                id: getNextMessageId(),
                "data-test-subj": "toastError",
              };
            }
          })
          .filter((item: any) => item);

        return [...arr, ...newMsgs];
      });
    });

    Emitter.on("displayInfo", (msg: any) => {
      setToastMessages((arr: any) => {
        if (arr.find((m: any) => m.text == msg)) {
          return arr;
        }
        //debugger;
        return [
          ...arr,
          {
            id: getNextMessageId(),
            title: "Info",
            color: "primary",
            iconType: "check",
            text: msg,
          },
        ];
      });
    });

    initializePostHog();
  }, []);

  return (
    <>
      {isDown && <DownOrOffline status={status} />}
      {!isDown && (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Page>{children}</Page>
          <EuiGlobalToastList
            toasts={toastMessages}
            dismissToast={(removed) => {
              setToastMessages((arr) => {
                return arr.filter((m: any) => m.id !== removed.id);
              });
            }}
            toastLifeTimeMs={toastLifeTimeMs}
            className={classes.toastCenter}
          />
        </ErrorBoundary>
      )}
    </>
  );
};

export default withRouter(Layout);
